// src/components/common/Combobox/Combobox.jsx

import React, { useState, useMemo, useRef, useEffect } from 'react';
import styles from './ComboBox.module.css';
import { FiSearch, FiX, FiChevronDown, FiCheck } from 'react-icons/fi';

const ComboBox = ({
    options = [],
    value,
    onChange,
    placeholder = 'Search...',
    label,
    required = false,
    disabled = false,
    loading = false,
    noResultsMessage = 'No results found',
    error = false,
    errorMessage = '',
    getOptionLabel = (option) => option.label || '',
    getOptionValue = (option) => option.id || option.value,
    getOptionKey = (option) => option.id || option.value,
    displayKey = 'label',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Find selected option
    const selectedOption = useMemo(() => {
        return options.find(opt => getOptionValue(opt) === value);
    }, [options, value, getOptionValue]);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return options;
        const term = searchTerm.toLowerCase().trim();
        return options.filter(opt => {
            const label = String(getOptionLabel(opt)).toLowerCase();
            return label.includes(term);
        });
    }, [options, searchTerm, getOptionLabel]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                if (!selectedOption) {
                    setSearchTerm('');
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedOption]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) {
                if (e.key === 'ArrowDown' || e.key === 'Enter') {
                    e.preventDefault();
                    setIsOpen(true);
                    setHighlightedIndex(-1);
                }
                return;
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex(prev =>
                        prev < filteredOptions.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                        const selected = filteredOptions[highlightedIndex];
                        if (selected) {
                            onChange(getOptionValue(selected));
                            setSearchTerm('');
                            setIsOpen(false);
                            setHighlightedIndex(-1);
                            inputRef.current?.focus();
                        }
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    if (!selectedOption) {
                        setSearchTerm('');
                    }
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, filteredOptions, highlightedIndex, onChange, selectedOption, getOptionValue]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('.dropdown-item');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleInputFocus = () => {
        if (!disabled && !loading) {
            setIsOpen(true);
            if (!selectedOption) {
                setSearchTerm('');
            }
            setHighlightedIndex(-1);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsOpen(true);
        setHighlightedIndex(-1);
        if (selectedOption) {
            onChange(null);
        }
    };

    const handleClear = () => {
        onChange(null);
        setSearchTerm('');
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const handleSelectOption = (option) => {
        onChange(getOptionValue(option));
        setSearchTerm('');
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const getDisplayText = () => {
        if (selectedOption) {
            return getOptionLabel(selectedOption);
        }
        return searchTerm;
    };

    return (
        <div className={styles.comboboxWrapper} ref={wrapperRef}>
            {label && (
                <label className={styles.comboboxLabel}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            <div className={`${styles.comboboxInputWrapper} ${error ? styles.error : ''}`}>
                <FiSearch className={styles.comboboxSearchIcon} size={18} />
                <input
                    ref={inputRef}
                    type="text"
                    value={getDisplayText()}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={selectedOption ? getOptionLabel(selectedOption) : placeholder}
                    disabled={disabled || loading}
                    className={styles.comboboxInput}
                    autoComplete="off"
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className={styles.comboboxClearButton}
                    >
                        <FiX size={16} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={styles.comboboxToggleButton}
                    disabled={disabled || loading}
                >
                    <FiChevronDown size={18} className={isOpen ? styles.rotated : ''} />
                </button>
                {loading && (
                    <span className={styles.comboboxSpinner}></span>
                )}
            </div>
            {error && errorMessage && (
                <div className={styles.comboboxError}>{errorMessage}</div>
            )}

            {isOpen && !disabled && !loading && (
                <div className={styles.comboboxDropdown} ref={listRef}>
                    {filteredOptions.length === 0 ? (
                        <div className={styles.comboboxNoResults}>
                            {noResultsMessage}
                        </div>
                    ) : (
                        filteredOptions.map((option, index) => (
                            <div
                                key={getOptionKey(option)}
                                className={`dropdown-item ${styles.comboboxItem} ${index === highlightedIndex ? styles.highlighted : ''} ${getOptionValue(option) === value ? styles.selected : ''}`}
                                onClick={() => handleSelectOption(option)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onMouseLeave={() => setHighlightedIndex(-1)}
                            >
                                <span className={styles.comboboxItemLabel}>
                                    {getOptionLabel(option)}
                                </span>
                                {getOptionValue(option) === value && (
                                    <FiCheck className={styles.comboboxItemCheck} size={16} />
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ComboBox;